// Auth Choice Modal - Sign In / Create Account options
// Shown when unauthenticated users try to apply for a job

import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Linking } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useDispatch } from 'react-redux';
import { LogIn, UserPlus, UserCircle, Building2, ChevronDown } from 'lucide-react';
import WebModal from './WebModal';
import { webColors } from '../../constants/webStyles';
import { setIsDoctor } from '../../reducers/auth/user';
import { APP_URLS, AppleIcon, GooglePlayIcon, HuaweiIcon } from '../../constants/appStoreUrls';

const AuthChoiceModal = ({ visible, onClose, jobTitle }) => {
  const navigation = useNavigation();
  const dispatch = useDispatch();
  const [expandedCard, setExpandedCard] = useState(null);

  const handleSignIn = () => {
    onClose();
    navigation.navigate('AuthScreen', { screen: 'LoginScreen' });
  };

  // Create an account on the web -> role-specific registration flow.
  // The top-level CTA defaults to professional, since job applicants are professionals.
  const handleCreateAccount = (isDoctorChoice) => {
    dispatch(setIsDoctor(isDoctorChoice));
    onClose();
    navigation.navigate(isDoctorChoice ? 'DoctorAuth' : 'FacilityAuth');
  };

  const toggleCard = (cardType) => {
    setExpandedCard(expandedCard === cardType ? null : cardType);
  };

  const handleDownloadApp = (userType, store) => {
    const url = APP_URLS[userType][store];
    Linking.openURL(url);
  };

  const handleContactSupport = () => {
    // Navigate to support or open support link
    Linking.openURL('mailto:support@supermedic.health');
  };

  const renderAppStoreButtons = (userType) => (
    <View style={styles.appStoreSection}>
      <Text style={styles.appStoreLabel}>Download our app to get started:</Text>
      <View style={styles.appStoreButtons}>
        {/* Apple App Store */}
        <TouchableOpacity
          style={styles.storeBtn}
          onPress={() => handleDownloadApp(userType, 'apple')}
        >
          <AppleIcon />
          <View style={styles.storeBtnText}>
            <Text style={styles.storeBtnSubtext}>Download on the</Text>
            <Text style={styles.storeBtnTitle}>App Store</Text>
          </View>
        </TouchableOpacity>

        {/* Google Play */}
        <TouchableOpacity
          style={styles.storeBtn}
          onPress={() => handleDownloadApp(userType, 'google')}
        >
          <GooglePlayIcon />
          <View style={styles.storeBtnText}>
            <Text style={styles.storeBtnSubtext}>Get it on</Text>
            <Text style={styles.storeBtnTitle}>Google Play</Text>
          </View>
        </TouchableOpacity>

        {/* Huawei AppGallery */}
        <TouchableOpacity
          style={[styles.storeBtn, styles.storeBtnHuawei]}
          onPress={() => handleDownloadApp(userType, 'huawei')}
        >
          <HuaweiIcon />
          <View style={styles.storeBtnText}>
            <Text style={styles.storeBtnSubtext}>Explore it on</Text>
            <Text style={styles.storeBtnTitle}>AppGallery</Text>
          </View>
        </TouchableOpacity>
      </View>
    </View>
  );

  const FooterNote = () => (
    <View style={styles.footerNote}>
      <Text style={styles.footerText}>
        Need help?{' '}
        <Text style={styles.footerLink} onPress={handleContactSupport}>
          Contact Support
        </Text>
      </Text>
    </View>
  );

  return (
    <WebModal
      visible={visible}
      onClose={onClose}
      title="Sign in to Apply"
      subtitle={jobTitle ? `Applying for: ${jobTitle}` : null}
      maxWidth={420}
      footer={<FooterNote />}
    >
      <View style={styles.content}>
        <Text style={styles.description}>
          To apply for this job, please sign in to your account or create a new one.
        </Text>

        {/* Sign In Option */}
        <TouchableOpacity style={styles.signInOption} onPress={handleSignIn}>
          <View style={[styles.optionIcon, styles.optionIconPink]}>
            <LogIn size={24} color={webColors.primary} />
          </View>
          <View style={styles.optionContent}>
            <Text style={styles.optionTitle}>Sign In</Text>
            <Text style={styles.optionDescription}>Already have an account? Sign in to continue</Text>
          </View>
        </TouchableOpacity>

        {/* Create Account (primary CTA - registers as a healthcare professional) */}
        <TouchableOpacity style={styles.createAccountTopBtn} onPress={() => handleCreateAccount(true)}>
          <UserPlus size={20} color="#fff" />
          <Text style={styles.createAccountTopBtnText}>Create an account</Text>
        </TouchableOpacity>

        <View style={styles.divider}>
          <View style={styles.dividerLine} />
          <Text style={styles.dividerText}>Or create an account</Text>
          <View style={styles.dividerLine} />
        </View>

        {/* Healthcare Professional Card */}
        <View style={[styles.userTypeCard, expandedCard === 'professional' && styles.userTypeCardExpanded]}>
          <TouchableOpacity
            style={styles.cardHeader}
            onPress={() => toggleCard('professional')}
          >
            <View style={[styles.optionIcon, styles.optionIconPink]}>
              <UserCircle size={24} color={webColors.primary} />
            </View>
            <View style={styles.optionContent}>
              <Text style={styles.optionTitle}>Healthcare Professional</Text>
              <Text style={styles.optionDescription}>I'm looking for healthcare jobs</Text>
            </View>
            <View style={[styles.cardArrow, expandedCard === 'professional' && styles.cardArrowExpanded]}>
              <ChevronDown size={20} color="#ccc" />
            </View>
          </TouchableOpacity>
          {expandedCard === 'professional' && (
            <>
              {renderAppStoreButtons('professional')}
              <View style={styles.webSignInSection}>
                <Text style={styles.webSignInLabel}>Or create an account on the web:</Text>
                <TouchableOpacity style={styles.webCreateBtn} onPress={() => handleCreateAccount(true)}>
                  <View style={[styles.optionIcon, styles.optionIconPink]}>
                    <UserPlus size={22} color={webColors.primary} />
                  </View>
                  <View style={styles.optionContent}>
                    <Text style={styles.webSignInTitle}>Create an account</Text>
                    <Text style={styles.webSignInSubtitle}>New here? Register as a healthcare professional</Text>
                  </View>
                </TouchableOpacity>
              </View>
            </>
          )}
        </View>

        {/* Employer / Facility Card */}
        <View style={[styles.userTypeCard, expandedCard === 'employer' && styles.userTypeCardExpanded]}>
          <TouchableOpacity
            style={styles.cardHeader}
            onPress={() => toggleCard('employer')}
          >
            <View style={[styles.optionIcon, styles.optionIconBlue]}>
              <Building2 size={24} color="#2563eb" />
            </View>
            <View style={styles.optionContent}>
              <Text style={styles.optionTitle}>Employer / Facility</Text>
              <Text style={styles.optionDescription}>I want to post jobs and hire professionals</Text>
            </View>
            <View style={[styles.cardArrow, expandedCard === 'employer' && styles.cardArrowExpanded]}>
              <ChevronDown size={20} color="#ccc" />
            </View>
          </TouchableOpacity>
          {expandedCard === 'employer' && (
            <>
              {renderAppStoreButtons('employer')}
              <View style={styles.webSignInSection}>
                <Text style={styles.webSignInLabel}>Or continue on the web:</Text>
                <View style={styles.webBtnGroup}>
                  <TouchableOpacity style={styles.webSignInBtn} onPress={handleSignIn}>
                    <View style={[styles.optionIcon, styles.optionIconPink]}>
                      <LogIn size={22} color={webColors.primary} />
                    </View>
                    <View style={styles.optionContent}>
                      <Text style={styles.webSignInTitle}>Sign In</Text>
                      <Text style={styles.webSignInSubtitle}>Already have an account? Sign in to continue</Text>
                    </View>
                  </TouchableOpacity>
                  <TouchableOpacity style={styles.webCreateBtn} onPress={() => handleCreateAccount(false)}>
                    <View style={[styles.optionIcon, styles.optionIconBlue]}>
                      <UserPlus size={22} color="#2563eb" />
                    </View>
                    <View style={styles.optionContent}>
                      <Text style={styles.webSignInTitle}>Create an account</Text>
                      <Text style={styles.webSignInSubtitle}>New here? Register your facility</Text>
                    </View>
                  </TouchableOpacity>
                </View>
              </View>
            </>
          )}
        </View>
      </View>
    </WebModal>
  );
};

const styles = StyleSheet.create({
  content: {
    gap: 14,
  },
  description: {
    fontSize: 15,
    color: '#666',
    lineHeight: 22,
    marginBottom: 6,
  },
  // Sign In Option
  signInOption: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    padding: 16,
    backgroundColor: '#fafafa',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#eee',
    cursor: 'pointer',
    marginBottom: 10,
  },
  // Create Account (top primary CTA)
  createAccountTopBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    padding: 16,
    backgroundColor: webColors.primary,
    borderRadius: 14,
    cursor: 'pointer',
  },
  createAccountTopBtnText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
  },
  // Option Icons with gradients
  optionIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  optionIconPink: {
    background: 'linear-gradient(135deg, #ffe4ef 0%, #ffd4e8 100%)',
    backgroundColor: '#ffe4ef', // Fallback
  },
  optionIconBlue: {
    background: 'linear-gradient(135deg, #e4f0ff 0%, #d4e8ff 100%)',
    backgroundColor: '#e4f0ff', // Fallback
  },
  optionContent: {
    flex: 1,
  },
  optionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1a1a1a',
    marginBottom: 2,
  },
  optionDescription: {
    fontSize: 13,
    color: '#888',
  },
  // Divider
  divider: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    marginVertical: 10,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: '#e5e5e5',
  },
  dividerText: {
    fontSize: 12,
    color: '#999',
    fontWeight: '500',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  // User Type Cards (Expandable)
  userTypeCard: {
    backgroundColor: '#fafafa',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#eee',
    overflow: 'hidden',
  },
  userTypeCardExpanded: {
    borderColor: '#ddd',
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    padding: 16,
    cursor: 'pointer',
  },
  cardArrow: {
    transition: 'transform 0.2s ease',
  },
  cardArrowExpanded: {
    transform: [{ rotate: '180deg' }],
  },
  // App Store Section
  appStoreSection: {
    padding: 18,
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0',
    backgroundColor: '#fff',
  },
  appStoreLabel: {
    fontSize: 13,
    color: '#666',
    fontWeight: '500',
    marginBottom: 14,
  },
  appStoreButtons: {
    gap: 10,
  },
  storeBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 12,
    paddingHorizontal: 16,
    backgroundColor: '#1a1a1a',
    borderRadius: 12,
    cursor: 'pointer',
  },
  storeBtnHuawei: {
    backgroundColor: '#c7112b',
  },
  // Web Sign In (Employer)
  webSignInSection: {
    padding: 18,
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0',
    backgroundColor: '#fff',
  },
  webSignInLabel: {
    fontSize: 13,
    color: '#666',
    fontWeight: '500',
    marginBottom: 14,
  },
  webSignInBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    padding: 14,
    paddingHorizontal: 16,
    backgroundColor: '#f5f5f5',
    borderRadius: 14,
    cursor: 'pointer',
  },
  webBtnGroup: {
    gap: 10,
  },
  webCreateBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    padding: 14,
    paddingHorizontal: 16,
    backgroundColor: '#f5f5f5',
    borderRadius: 14,
    cursor: 'pointer',
  },
  webSignInTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: '#111',
  },
  webSignInSubtitle: {
    fontSize: 13,
    color: '#888',
    lineHeight: 18,
  },
  storeBtnText: {
    flexDirection: 'column',
  },
  storeBtnSubtext: {
    fontSize: 10,
    color: '#aaa',
    lineHeight: 14,
  },
  storeBtnTitle: {
    fontSize: 14,
    color: '#fff',
    fontWeight: '600',
    lineHeight: 18,
  },
  // Footer Note
  footerNote: {
    alignItems: 'center',
    paddingVertical: 8,
  },
  footerText: {
    fontSize: 12,
    color: '#999',
  },
  footerLink: {
    color: webColors.primary,
    fontWeight: '500',
    cursor: 'pointer',
  },
});

export default AuthChoiceModal;
